import React, { useCallback } from "react";
import { ExclamationTriangleIcon } from "@heroicons/react/24/solid";
import {
  GetProviderName,
  type GPUInstanceType,
} from "types/InstanceTypes";
import type { CreateFirewallRule } from "components/Environment/Create/ExposePorts";
import { displayPlatformType } from "components/Environment/utils";
import CannotStopWarning from "components/Environment/Create/ComputeSelector/RackCard/CannotStopWarning";
import { ExpandableConfigurationSection } from "../ExpandableConfigurationSection";
import { CloudTabContent } from "./CloudTabContent";
import { getComputeHardwareSummary } from "./computeSummary";
import { hasInstanceSelectionChanged } from "./instanceSelection";
import { getArchitectureMismatchWarning } from "utils/architecture";

interface ComputeConfigurationSectionProps {
  instanceType: GPUInstanceType | null;
  defaultInstanceType: GPUInstanceType | null;
  intendedInstanceType: string | null;
  intendedDiskStorage: string | null;
  instanceStorage: string | null;
  instanceLoading: boolean;
  intendedCloudCredentialId: string | null;
  intendedFirewallRules: CreateFirewallRule[] | null;
  imageId?: string | null;
  allInstances: GPUInstanceType[];
  selectedLocation: string | null;
  providerDiffers: boolean;
  isOutOfStock: boolean;
  open: boolean;
  onToggle: () => void;
  onShowEditComputeModal: () => void;
  onInstanceChange?: (
    instanceType: GPUInstanceType | null,
    diskStorage: string,
  ) => void;
  onStorageValidityChange: (valid: boolean) => void;
  onOutOfStockChange: (outOfStock: boolean) => void;
  onProviderDifferenceChange: (differs: boolean) => void;
  onLocationChange: (location: string | null) => void;
}

const ComputeWarning: React.FC<{
  children: React.ReactNode;
  tone?: "warning" | "error";
}> = ({ children, tone = "warning" }) => (
  <div
    className={`flex items-start gap-1.5 ${
      tone === "error" ? "text-red-500" : "text-yellow-500"
    }`}
  >
    <ExclamationTriangleIcon
      aria-hidden="true"
      className="mt-0.5 h-4 w-4 shrink-0"
    />
    <p>{children}</p>
  </div>
);

export const ComputeConfigurationSection: React.FC<
  ComputeConfigurationSectionProps
> = ({
  instanceType,
  defaultInstanceType,
  intendedInstanceType,
  intendedDiskStorage,
  instanceStorage,
  instanceLoading,
  intendedCloudCredentialId,
  intendedFirewallRules,
  imageId,
  allInstances,
  selectedLocation,
  providerDiffers,
  isOutOfStock,
  open,
  onToggle,
  onShowEditComputeModal,
  onInstanceChange,
  onStorageValidityChange,
  onOutOfStockChange,
  onProviderDifferenceChange,
  onLocationChange,
}) => {
  const providerWarning = providerDiffers
    ? "This cloud provider differs from the creator's original selection. The launchable may not work as expected on a different provider."
    : undefined;
  const outOfStockWarning = isOutOfStock
    ? "This instance type is currently out of stock. Please select a different instance type or check back later."
    : undefined;
  const architectureMismatchWarning = getArchitectureMismatchWarning(
    instanceType,
    defaultInstanceType,
  );
  const collapsedSummaryWarning =
    architectureMismatchWarning || outOfStockWarning || providerWarning ? (
      <>
        {architectureMismatchWarning && (
          <ComputeWarning>{architectureMismatchWarning}</ComputeWarning>
        )}
        {outOfStockWarning && (
          <ComputeWarning tone="error">{outOfStockWarning}</ComputeWarning>
        )}
        {providerWarning && <ComputeWarning>{providerWarning}</ComputeWarning>}
      </>
    ) : undefined;
  const expandedSummaryWarning =
    architectureMismatchWarning || providerWarning ? (
      <>
        {architectureMismatchWarning && (
          <ComputeWarning>{architectureMismatchWarning}</ComputeWarning>
        )}
        {providerWarning && <ComputeWarning>{providerWarning}</ComputeWarning>}
      </>
    ) : undefined;
  const providerName = instanceType
    ? displayPlatformType(GetProviderName(instanceType) || "Unknown")
    : null;
  const isDefaultInstanceTypeUnavailable =
    !instanceType && Boolean(intendedInstanceType);
  const hardwareSummary = instanceType
    ? getComputeHardwareSummary(instanceType)
    : isDefaultInstanceTypeUnavailable
      ? ["Default instance type unavailable"]
      : getComputeHardwareSummary(instanceType);
  const hasGpu = Boolean(instanceType?.supported_gpus?.[0]);
  const handleCloudInstanceSelect = useCallback(
    (nextInstance: GPUInstanceType | null, storage: string) => {
      if (hasInstanceSelectionChanged(instanceType, nextInstance)) {
        onLocationChange(null);
      }
      onInstanceChange?.(nextInstance, storage);
    },
    [instanceType, onInstanceChange, onLocationChange],
  );
  const summary = (
    <span className="inline-flex flex-wrap items-center">
      <span>
        {hardwareSummary.map((detail, index) => (
          <React.Fragment key={detail}>
            {index > 0 && " · "}
            <span
              className={
                index === 0 && hasGpu
                  ? "text-highlight"
                  : isDefaultInstanceTypeUnavailable
                    ? "text-red-500"
                    : undefined
              }
            >
              {detail}
            </span>
          </React.Fragment>
        ))}
        {providerName && <> · {providerName}</>}
      </span>
      {instanceType && !instanceType.stoppable && (
        <span className="ml-2 inline-flex items-center">
          <CannotStopWarning alwaysColored />
        </span>
      )}
    </span>
  );

  return (
    <ExpandableConfigurationSection
      id="launchable-compute"
      title="Compute"
      summary={summary}
      summaryWarning={open ? undefined : collapsedSummaryWarning}
      summaryWarningTone={outOfStockWarning ? "error" : "warning"}
      actionLabel="Change"
      disabled={instanceLoading}
      open={!instanceLoading && open}
      onToggle={onToggle}
    >
      <CloudTabContent
        instanceTypeObject={instanceType}
        intendedInstanceType={intendedInstanceType}
        intendedDiskStorage={intendedDiskStorage}
        instanceStorage={instanceStorage}
        intendedCloudCredId={intendedCloudCredentialId}
        intendedFirewallRules={intendedFirewallRules}
        imageId={imageId}
        summaryWarning={open ? expandedSummaryWarning : undefined}
        onShowEditComputeModal={onShowEditComputeModal}
        onCloudInstanceSelect={handleCloudInstanceSelect}
        onStorageValidityChange={onStorageValidityChange}
        onIsOutOfStock={onOutOfStockChange}
        onProviderDifferenceChange={onProviderDifferenceChange}
        allInstancesAvailable={allInstances}
        selectedLocation={selectedLocation}
        onLocationChange={onLocationChange}
      />
    </ExpandableConfigurationSection>
  );
};
